import { type SerializedError } from '@reduxjs/toolkit'
import { type FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { errorNotice, type SaveBarNotice, successNotice } from '../../../components/SaveBarNotice/saveBarNotice'
import { extractApiErrors } from '../../../models/ApiError'

export const STALE_TITLE = 'Update the booking page first to use your latest changes.'

export type LinkNoun = 'booking' | 'reschedule'

// A 409 carries a business rule the advisor can act on (e.g. every client has unsubscribed),
// so its message is shown verbatim instead of a generic failure notice.
export const conflictNotice = (error: FetchBaseQueryError | SerializedError | undefined): SaveBarNotice | undefined => {
  if (!error || !('status' in error) || error.status !== 409 || !('data' in error) || !error.data) {
    return undefined
  }
  const [first] = extractApiErrors(error)
  return first ? errorNotice(first.message, { withContactSupport: false }) : undefined
}

export const saveBarNotices = {
  linkCopied: (noun: LinkNoun, linkCount = 1): SaveBarNotice => {
    if (noun === 'reschedule') {
      return successNotice(linkCount > 1 ? 'Reschedule links copied' : 'Reschedule link copied')
    }
    return successNotice(linkCount > 1 ? 'Booking links copied' : 'Booking link copied')
  },
  emailCopied: (): SaveBarNotice => successNotice('Email copied'),
  pushedToDraft: (draftCount: number): SaveBarNotice =>
    successNotice(draftCount > 1 ? 'Invitation emails pushed to draft' : 'Invitation email pushed to draft'),
  partialDrafts: (created: number, total: number): SaveBarNotice => successNotice(`Created ${created} of ${total} drafts — the rest could not be created.`),
  bookingPageUpdated: (): SaveBarNotice => successNotice('Booking page updated'),
  sendEmailsError: (clientCount: number): SaveBarNotice =>
    errorNotice(clientCount > 1 ? 'Something went wrong sending these invitation emails.' : 'Something went wrong sending this invitation email.'),
  sendInviteError: (): SaveBarNotice => errorNotice('Something went wrong sending this invite.'),
  copyLinkError: (noun: LinkNoun): SaveBarNotice => errorNotice(`Something went wrong copying this ${noun} link.`),
  pushToDraftError: (clientCount: number): SaveBarNotice =>
    errorNotice(
      clientCount > 1 ? 'Something went wrong pushing these invitation emails to draft.' : 'Something went wrong pushing this invitation email to draft.',
    ),
  saveBookingError: (): SaveBarNotice => errorNotice('Something went wrong saving this booking.', { withContactSupport: false }),
}
